// FILE: src/Components/CreateGroup/CreateBrandWorkflowModal.js
import React, { useState } from 'react';
import { Modal, message, Input, Button } from 'antd';
import { useDispatch } from 'react-redux';
import { createGroup } from '../../services/user.profile';
import UpgradeModal from '../BuySubscription/UpgradeModal';
import { fetchGroups, setSelectedGroup } from '../../store/Slices/GroupSlice';

export default function CreateBrandWorkflowModal({
  isOpen,
  onClose,
  onBrandCreateComplete,
}) {
  const [brandName, setBrandName] = useState('');
  const [website, setWebsite] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState({
    visible: false,
    message: '',
    featureName: '',
  });

  const dispatch = useDispatch();

  const resetForm = () => {
    setBrandName('');
    setWebsite('');
    setIsLoading(false);
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  const handleCreateBrand = async () => {
    if (!brandName.trim() || !website.trim()) {
      message.error('Brand Name and Website are required.');
      return;
    }

    if (!website.includes('.')) {
      message.error('Please enter a valid website URL.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await createGroup({
        groupName: brandName.trim(),
        website: website.trim(),
      });

      if (response && response.success && response.group?._id) {
        const createdBrandName = response.group.name || brandName.trim();

        await dispatch(fetchGroups());
        dispatch(setSelectedGroup(createdBrandName));

        message.success(
          'Brand created successfully. AI analysis is running in the background.'
        );

        resetForm();

        if (typeof onBrandCreateComplete === 'function') {
          onBrandCreateComplete(createdBrandName, 'background_processing');
        }

        onClose();
      } else {
        message.error(response?.error || 'Failed to create brand.');
      }
    } catch (error) {
      if (
        error.response?.data?.errorType === 'LIMIT_REACHED'
      ) {
        setUpgradeModal({
          visible: true,
          message: error.response.data.message,
          featureName: 'Brands',
        });
      } else {
        message.error(
          error.message || 'An error occurred during brand creation.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Modal
        title={
          <div className="brand-modal-title">
            <span className="brand-modal-title-icon" aria-hidden="true">
              <i className="fa-solid fa-plus"></i>
            </span>
            <div className="min-w-0">
              <h2>Create a new brand</h2>
              <p>Set up a new workspace for your brand.</p>
            </div>
          </div>
        }
        open={isOpen}
        onCancel={handleCancel}
        footer={[
          <Button
            key="back"
            onClick={handleCancel}
            disabled={isLoading}
            className="brand-modal-secondary-button"
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={isLoading}
            onClick={handleCreateBrand}
            className="brand-modal-primary-button"
          >
            Create brand
          </Button>,
        ]}
        closable
        maskClosable={!isLoading}
        centered
        width={560}
        className="brand-create-modal"
        rootClassName="brand-modal-root"
        destroyOnClose
      >
        <div className="brand-create-content">
          <div className="brand-create-intro">
            <span className="brand-create-intro-icon" aria-hidden="true">
              <i className="fa-solid fa-wand-magic-sparkles"></i>
            </span>
            <p>
              We&apos;ll create the brand immediately, then analyze its website
              in the background to begin building brand context.
            </p>
          </div>

          <div className="brand-create-fields">
            <div className="brand-modal-field">
              <label htmlFor="new-brand-name">Brand name</label>
              <Input
                id="new-brand-name"
                prefix={<i className="fa-regular fa-building"></i>}
                placeholder="e.g., Oolook"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                disabled={isLoading}
                className="brand-modal-input"
                autoComplete="organization"
              />
            </div>

            <div className="brand-modal-field">
              <label htmlFor="new-brand-website">Brand website</label>
              <Input
                id="new-brand-website"
                prefix={<i className="fa-solid fa-globe"></i>}
                placeholder="https://example.com"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                disabled={isLoading}
                onPressEnter={handleCreateBrand}
                className="brand-modal-input"
                autoComplete="url"
              />
              <p className="brand-modal-field-help">
                Use the primary public website for this brand.
              </p>
            </div>
          </div>
        </div>
      </Modal>

      <UpgradeModal
        isOpen={upgradeModal.visible}
        onClose={() =>
          setUpgradeModal({ visible: false, message: '', featureName: '' })
        }
        message={upgradeModal.message}
        featureName={upgradeModal.featureName}
      />
    </>
  );
}